// Shared helpers for every file input / selection / drop in the web Creator.
//
// The browser exposes two different ways to get files from a drop:
//   - `DataTransfer.files`  — a flat FileList that NEVER recurses into folders.
//   - `DataTransferItem.webkitGetAsEntry()` — the File & Directory Entries API,
//     which DOES let us walk dropped folders.
// `readDataTransferFiles` uses the entries API (with a `.files` fallback) so that
// dropping a FOLDER works the same as dropping its files individually. The
// extension-filter + numeric-sort helpers are shared with the click-to-pick path
// (ElectronPolyfill.showOpenDialog) so selection and drop behave identically.
//
// This module intentionally imports nothing from the app (no ElectronPolyfill /
// utils/file) so it can be consumed everywhere without circular dependencies.

/**
 * Read the files from a drop's DataTransfer. A dropped folder contributes the
 * files sitting DIRECTLY inside it (one level deep) — nested subfolders are not
 * traversed. Returns a flat `File[]`. Falls back to the flat `dataTransfer.files`
 * list when the entries API is unavailable.
 *
 * Safe to call as the first `await` inside a `drop` handler: it reads
 * `dataTransfer.items` synchronously (that collection is only valid during the
 * event) before yielding to the async folder read.
 *
 * @param {DataTransfer} dataTransfer
 * @returns {Promise<File[]>}
 */
export async function readDataTransferFiles(dataTransfer) {
  if (!dataTransfer) return [];

  const items = dataTransfer.items;
  const supportsEntries =
    items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function';

  if (!supportsEntries) {
    return Array.from(dataTransfer.files || []);
  }

  // Gather the entries up front — `items` becomes invalid after the first await.
  const entries = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind && item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry();
    if (entry) entries.push(entry);
  }

  // No entries (older browser / non-filesystem source) — use the flat list.
  if (entries.length === 0) {
    return Array.from(dataTransfer.files || []);
  }

  const out = [];
  await Promise.all(entries.map(entry => collectEntry(entry, out)));
  return out;
}

// Resolve a FileSystemFileEntry to a File (null if unreadable, so one bad file
// doesn't reject the whole drop).
function fileFromEntry(fileEntry) {
  return new Promise(resolve => {
    fileEntry.file(
      file => resolve(file),
      () => resolve(null),
    );
  });
}

// Read a directory's IMMEDIATE file children (one level deep). Subdirectories are
// intentionally NOT descended into — a dropped folder contributes the files
// sitting directly inside it, nothing from nested subfolders.
function readDirectoryFiles(dirEntry, out) {
  return new Promise(resolve => {
    const reader = dirEntry.createReader();
    // readEntries returns directory contents in batches (Safari/Chrome cap ~100)
    // — keep reading until it returns an empty batch.
    const readBatch = () => {
      reader.readEntries(
        batch => {
          if (!batch.length) {
            resolve();
            return;
          }
          const fileEntries = batch.filter(e => e.isFile); // ignore subdirectories
          Promise.all(
            fileEntries.map(fe => fileFromEntry(fe).then(file => { if (file) out.push(file); })),
          ).then(readBatch);
        },
        () => resolve(),
      );
    };
    readBatch();
  });
}

// Collect File objects from a top-level dropped entry into `out`: a file yields
// itself; a directory yields its immediate files only (no recursion).
function collectEntry(entry, out) {
  if (entry.isFile) {
    return fileFromEntry(entry).then(file => { if (file) out.push(file); });
  }
  if (entry.isDirectory) {
    return readDirectoryFiles(entry, out);
  }
  return Promise.resolve();
}

/** Extension of a filename, lowercased, with leading dot ('Frame.PNG' -> '.png'). */
export function fileExtension(name) {
  const str = String(name);
  const dot = str.lastIndexOf('.');
  return dot >= 0 ? str.slice(dot).toLowerCase() : '';
}

/** Normalize 'png' or '.PNG' to '.png'. */
function normalizeExtension(ext) {
  const e = String(ext).toLowerCase();
  return e.startsWith('.') ? e : `.${e}`;
}

/**
 * Keep only files whose extension is in `extensions` (each given with or without
 * a leading dot, case-insensitive). Falsy entries are ignored so callers can pass
 * e.g. `[..., zipAllowed ? '.zip' : null]`. Filtering (rather than hard-rejecting
 * on the first stray file) is what makes folder drops practical — folders carry
 * junk like `.DS_Store` / `Thumbs.db`.
 *
 * @param {File[]} files
 * @param {Array<string|null|undefined>} extensions
 * @returns {File[]}
 */
export function filterFilesByExtensions(files, extensions) {
  const allowed = new Set(extensions.filter(Boolean).map(normalizeExtension));
  return Array.from(files).filter(f => allowed.has(fileExtension(f.name)));
}

/**
 * Numeric, name-based sort so image sequences order naturally
 * (frame_2 before frame_10).
 *
 * @param {File[]} files
 * @returns {File[]}
 */
export function sortFilesNumerically(files) {
  return Array.from(files).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
}

/**
 * Keep only files sitting DIRECTLY inside the chosen folder (one level deep),
 * dropping anything from nested subfolders. For a `<input webkitdirectory>`
 * selection each file carries a `webkitRelativePath` of "<folder>/<file>" for a
 * direct child and "<folder>/<sub>/.../<file>" for nested ones, so a direct child
 * has exactly two path segments. Files without a relative path (not a directory
 * pick) are kept as-is.
 *
 * @param {File[]} files
 * @returns {File[]}
 */
export function filterTopLevelFiles(files) {
  return Array.from(files).filter(f => {
    const rel = f.webkitRelativePath || '';
    return rel === '' || rel.split('/').length <= 2;
  });
}

/**
 * Install the page-level guard that stops the browser from opening / navigating
 * to a file dropped outside an explicit drop zone. Call from a view's mounted().
 * Mirrors the historic per-page handlers (direct assignment, so the most recently
 * mounted view wins — behaviour unchanged from the inline versions it replaces).
 */
export function preventPageFileDrop() {
  document.ondragover = e => {
    e.preventDefault();
    return false;
  };
  document.ondrop = e => {
    e.preventDefault();
    return false;
  };
}
